import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ImageOff } from "lucide-react";
import { useState } from "react";

function StatusBadge({ status }) {
  if (status === "found") {
    return (
      <Badge
        className="bg-[#DCFCE7] text-[#16A34A] hover:bg-[#DCFCE7] border-0 rounded-sm font-mono text-[10px] tracking-widest"
        data-testid="status-found"
      >
        OK
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FEE2E2] border-0 rounded-sm font-mono text-[10px] tracking-widest"
      data-testid="status-not-found"
    >
      NÃO ENC.
    </Badge>
  );
}

function IndicationChip({ text }) {
  if (!text) return <span className="text-zinc-400">—</span>;
  const isName = text.includes("Nome");
  const isCategory = text.includes("Categoria");
  const cls = isName && isCategory
    ? "bg-blue-50 text-blue-700"
    : isCategory
    ? "bg-indigo-50 text-indigo-700"
    : isName
    ? "bg-amber-50 text-amber-700"
    : "bg-zinc-50 text-zinc-600";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] tracking-wider font-mono ${cls}`}>
      {text}
    </span>
  );
}

function Thumb({ src }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="w-12 h-12 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center">
        <ImageOff className="w-4 h-4 text-zinc-400" />
      </div>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      title={src}
      className="block w-12 h-12 rounded-md overflow-hidden border border-zinc-200 bg-white hover:ring-2 hover:ring-[#002FA7] transition-all"
    >
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setBroken(true)}
      />
    </a>
  );
}

export default function ResultsTable({ results }) {
  if (!results || results.length === 0) return null;
  return (
    <div className="border border-zinc-200 rounded-md overflow-hidden bg-white overflow-x-auto">
      <Table data-testid="results-table">
        <TableHeader>
          <TableRow className="bg-zinc-50 border-b border-zinc-200 hover:bg-zinc-50">
            <TableHead className="eyebrow text-zinc-500 w-[200px]">Nome Informado</TableHead>
            <TableHead className="eyebrow text-zinc-500 w-[130px]">Categoria</TableHead>
            <TableHead className="eyebrow text-zinc-500">Descrição</TableHead>
            <TableHead className="eyebrow text-zinc-500 w-[200px]">Imagens</TableHead>
            <TableHead className="eyebrow text-zinc-500 w-[150px]">Indicação</TableHead>
            <TableHead className="eyebrow text-zinc-500 w-[90px]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((r, i) => (
            <TableRow
              key={(r.input_name || "row") + i}
              className="border-b border-zinc-100 hover:bg-zinc-50/60"
              data-testid={`result-row-${i}`}
            >
              <TableCell className="text-sm text-zinc-900 py-4 align-top max-w-[200px]">
                <div className="truncate font-medium" title={r.input_name || ""}>
                  {r.input_name || <span className="text-zinc-400">—</span>}
                </div>
              </TableCell>
              <TableCell className="text-xs text-zinc-500 py-4 align-top font-mono uppercase tracking-wider">
                {r.input_category || <span className="text-zinc-300">—</span>}
              </TableCell>
              <TableCell className="text-sm text-zinc-700 py-4 align-top max-w-[320px]">
                <div className="truncate" title={r.description || r.name || ""}>
                  {r.description || r.name || <span className="text-zinc-400">—</span>}
                </div>
              </TableCell>
              <TableCell className="py-4 align-top">
                <div className="flex gap-2">
                  <Thumb src={r.images?.[0]} />
                  <Thumb src={r.images?.[1]} />
                  <Thumb src={r.images?.[2]} />
                </div>
              </TableCell>
              <TableCell className="py-4 align-top">
                <div className="space-y-1">
                  <IndicationChip text={r.indication} />
                  <div className="text-[10px] text-zinc-400 font-mono">{r.source || ""}</div>
                </div>
              </TableCell>
              <TableCell className="py-4 align-top">
                <StatusBadge status={r.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
